/**
 * taxCardMarker — read the in-band `|taxcard|` marker out of a Tax Planning V2 bot reply.
 *
 * ⚠️ MUST stay in sync with the backend writer/reader:
 *   BAL/BAL/AppBAL/LHB/TaxCardMarkerV2.cs → `AppendMarker` / `SplitMarker` / `FirstBalanced`.
 * That class is the source of truth; this module is a straight port of `SplitMarker`, including
 * everything it tolerates. A working JS reference already exists in
 *   scripts/lhb-chat-sim.html → `balancedAt` / `parseReply`
 * and this file deliberately reproduces it rather than inventing a second algorithm.
 *
 * Wire shape (one marker per card, cards BEFORE the chips — ordering is part of the contract):
 *   <text> \n\n |taxcard|({ … }) \n\n <คำถามถัดไป> \n\n |quickReply|[ … ]
 *
 * 🔴 WHY BRACE DEPTH AND NEVER A REGEX.
 * The payload is scanned by counting `{`/`}` (or `[`/`]` for the legacy array form) while skipping
 * string literals, with `\"` not ending a literal. Two independent reasons, both from real data:
 *   1. Thai card copy is full of parentheses — "(ก) บ้าน โรงเรือน", "เต็มปี (12 เดือน)",
 *      "(เฉพาะที่ดินในการเกษตรกรรม หักเหมา 20%)". Counting the token's own parens would end the
 *      payload in the middle of a customer-authored label.
 *   2. A card nests arrays several levels deep (categories[] → sections[] → items[]), and a reply
 *      can carry a second marker plus `|quickReply|[…]` afterwards. The naive greedy
 *      `match(/\[[\s\S]*\]/)` runs to the LAST `]` in the whole message, swallows the neighbouring
 *      payload, and then JSON.parse throws — chips and cards vanish silently. That exact bug is what
 *      TaxCardMarkerV2's ordering rule works around on old clients; this side must not reproduce it.
 *
 * 🔴 WHY `text` IS THE PART BEFORE **AND** AFTER THE MARKER, REJOINED.
 * The card is a SEPARATOR, not a terminator: the node puts the NEXT QUESTION after the marker on
 * purpose, so a client can render "card bubble, then question bubble" by splitting on this one
 * marker (TaxCardMarkerV2.cs → SplitMarker, the 🔴 note above `text`). The obvious reading —
 * `raw.split(marker)[0]` — silently swallows that question everywhere a message is stripped for
 * display. Each surviving piece is trimmed, empties dropped, and the rest joined with '\n\n',
 * exactly like the C# `string.Join("\n\n", text.Select(Trim).Where(len > 0))`.
 *
 * Nothing here throws: this screen exists partly to SEE broken payloads, so a card whose JSON does
 * not parse is still returned (with `doc: null`) instead of being dropped.
 *
 * Pure logic (NO Vue / framework import) so it can be unit-tested under plain Node —
 * see tests/taxCardMarker.test.mjs.
 */

/** The in-band marker, lower-case canonical form. Matched case-insensitively (see below). */
export const TAX_CARD_MARKER = '|taxcard|';

/**
 * Case-insensitive `indexOf` for the marker.
 *
 * 🔴 Deliberately NOT `raw.toLowerCase().indexOf(...)`: lower-casing can change a string's LENGTH
 * for a few code points (e.g. 'İ' U+0130 → 'i̇'), which shifts every index after it and would slice
 * the payload one character off. Comparing a fixed-width window of the ORIGINAL string keeps every
 * index valid. Legacy transcripts spell it `|taxCard|`, so case-insensitivity is not optional.
 */
function indexOfMarker(source, fromIndex) {
  const len = TAX_CARD_MARKER.length;
  for (let i = Math.max(0, fromIndex); i + len <= source.length; i += 1) {
    if (source[i] !== '|') continue;
    if (source.slice(i, i + len).toLowerCase() === TAX_CARD_MARKER) return i;
  }
  return -1;
}

/**
 * The balanced `open`…`close` span starting EXACTLY at `start`, matched by depth.
 * Port of TaxCardMarkerV2.FirstBalanced.
 *
 * String literals are skipped over: a `}` or `]` inside a Thai label must not close the span, and
 * `\"` inside that literal must not end the literal early.
 *
 * @returns {string|null} the span including its delimiters, or null when unterminated —
 *   never a partial payload.
 */
function firstBalanced(source, start, open, close) {
  if (!source || start >= source.length || source[start] !== open) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < source.length; i += 1) {
    const c = source[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (c === '\\') escaped = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"') { inString = true; continue; }
    if (c === open) depth += 1;
    else if (c === close) {
      depth -= 1;
      if (depth === 0) return source.slice(start, i + 1);
    }
  }
  return null; // unterminated ⇒ no payload, never a partial one
}

function isWhitespace(ch) {
  return ch !== undefined && /\s/.test(ch);
}

/** JSON.parse that answers `null` instead of throwing — a broken payload is data here, not an error. */
function safeParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * @typedef {Object} TaxCardHit
 * @property {string} raw       The card's own raw JSON text (what to show in a "ดู JSON" panel).
 * @property {Object|null} doc  JSON.parse of `raw`, or null when the payload does not parse.
 */

/**
 * Split a bot reply into display text + the cards it carried.
 *
 * Tolerated payload forms, all of them present in stored transcripts:
 *   `|taxcard|({ … })`  current token (parentheses are part of it, not decoration)
 *   `|taxcard|{ … }`    bare object
 *   `|taxcard|[{…},{…}]` legacy ARRAY — each ELEMENT becomes its own card, so a turn mixing the old
 *                        and new spellings still comes back as one flat list.
 *
 * @param {string} raw Reply text, possibly carrying zero or more markers.
 * @returns {{ text: string, cards: TaxCardHit[] }}
 *   No marker (or nothing parseable after one) ⇒ `{ text: raw, cards: [] }` with `raw` UNCHANGED —
 *   not trimmed — because "this text says nothing about cards" must not also reformat the message.
 */
export function splitTaxCards(raw) {
  const source = raw == null ? '' : String(raw);
  if (!source) return { text: source, cards: [] };

  /** @type {TaxCardHit[]} */
  const cards = [];
  const pieces = []; // the message with every marker block cut out
  let cursor = 0;
  let matched = 0; // markers actually CONSUMED — not the same as cards.length (an empty array yields none)

  for (;;) {
    const idx = indexOfMarker(source, cursor);
    if (idx < 0) break;

    let p = idx + TAX_CARD_MARKER.length;
    while (isWhitespace(source[p])) p += 1;

    const paren = source[p] === '(';
    if (paren) {
      p += 1;
      while (isWhitespace(source[p])) p += 1;
    }

    const body = source[p] === '['
      ? firstBalanced(source, p, '[', ']')
      : firstBalanced(source, p, '{', '}');
    // 🔴 Unterminated ⇒ stop here and leave the remainder as visible text. Never hand back half a
    // payload: half a card renders as a lie, whereas visible JSON debris is loud and obvious.
    if (body === null) break;

    let end = p + body.length;
    if (paren) {
      let q = end;
      while (isWhitespace(source[q])) q += 1;
      if (source[q] === ')') end = q + 1;
    }

    if (body[0] === '[') {
      const parsed = safeParse(body);
      if (Array.isArray(parsed)) {
        // Legacy array ⇒ its ELEMENTS. `raw` per card is re-serialised: the element's exact source
        // span is not tracked, and this string is only ever shown in the JSON inspector.
        parsed.forEach((item) => cards.push({ raw: JSON.stringify(item), doc: item }));
      } else {
        cards.push({ raw: body, doc: null }); // unparseable array: keep it visible, do not drop it
      }
    } else {
      cards.push({ raw: body, doc: safeParse(body) });
    }

    pieces.push(source.slice(cursor, idx));
    cursor = end;
    matched += 1;
  }

  if (matched === 0) return { text: source, cards: [] };

  pieces.push(source.slice(cursor));
  const text = pieces.map((piece) => piece.trim()).filter((piece) => piece.length > 0).join('\n\n');
  return { text, cards };
}

/**
 * Parse the DTO's `turn.taxCards` field — a JSON ARRAY string written by
 * TaxCardMarkerV2.SplitMarker's `payload` (`"[" + docs.join(",") + "]"`).
 *
 * Empty / null / unparseable ⇒ `[]`. A card list is decoration on a message that was already
 * delivered, so a bad string must never take the turn down with it.
 *
 * @param {string|Array|null|undefined} json
 * @returns {Object[]} card envelopes, in wire order.
 */
export function parseTaxCardsField(json) {
  if (json == null) return [];

  // Already-decoded arrays are accepted too: some callers hand over an axios-parsed field.
  const parsed = Array.isArray(json) ? json : safeParse(String(json).trim() || 'null');
  if (parsed == null) return [];

  const list = Array.isArray(parsed) ? parsed : [parsed];
  return list.filter((item) => item != null && typeof item === 'object');
}

export default splitTaxCards;
